// PATH: src/components/layout/Footer/Footer.jsx
import React, { useCallback, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme } from "app/providers/ThemeProvider";
import "./Footer.css";

const ROUTES = {
  home: "/",
  nosotros: "/nosotros",
  servicios: "/servicios",
  contacto: "/contacto",
  contactoSeccion: "/contacto/seccion",
  alianzasSeccion: "/alianzas/seccion",
};

function getScrollHost() {
  const container = document.querySelector(".App");
  return { container, host: container || window };
}

function getNavHeight() {
  if (typeof window === "undefined") return 76;
  const raw = window.getComputedStyle(document.documentElement).getPropertyValue("--navbar-height");
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 76;
}

function scrollToTop(behavior = "smooth") {
  const { container } = getScrollHost();
  if (container) container.scrollTo({ top: 0, behavior });
  else window.scrollTo({ top: 0, behavior });
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return false;

  const navH = getNavHeight();
  const { container } = getScrollHost();

  if (container) {
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = elRect.top - containerRect.top + container.scrollTop - navH;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return true;
  }

  const top = el.getBoundingClientRect().top + window.scrollY - navH;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  return true;
}

export default function Footer() {
  const { darkMode } = useTheme();
  const location = useLocation();
  const year = new Date().getFullYear();

  const isHome = location.pathname === ROUTES.home;

  const socials = useMemo(
    () => [
      { label: "LinkedIn", href: "https://www.linkedin.com/", text: "in" },
      { label: "Instagram", href: "https://www.instagram.com/", text: "ig" },
      { label: "Facebook", href: "https://www.facebook.com/", text: "fb" },
    ],
    []
  );

  const onHomeScroll = useCallback(
    (id) => () => {
      if (id === "top") scrollToTop("smooth");
      else scrollToId(id);
    },
    []
  );

  return (
    <footer className={`Footer ${darkMode ? "is-dark" : "is-light"}`} aria-label="Pie de página">
      <div className="Footer__topDivider" aria-hidden="true" />

      <div className="Footer__container">
        <div className="Footer__brand">
          <div className="Footer__brandTitleRow">
            <span className="Footer__brandMark" aria-hidden="true" />
            <h2 className="Footer__brandTitle">Grupo Arca</h2>
          </div>

          <p className="Footer__brandDesc">
            Construcción, obra civil e infraestructura con control de calidad, seguridad y cumplimiento en cada entrega.
          </p>

          <div className="Footer__social" aria-label="Redes sociales">
            {socials.map((s) => (
              <a
                key={s.label}
                className="Footer__socialBtn"
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
              >
                {s.text}
              </a>
            ))}
          </div>
        </div>

        <nav className="Footer__col" aria-label="Navegación">
          <p className="Footer__colTitle">Navegación</p>
          <ul className="Footer__list">
            <li>
              {isHome ? (
                <button type="button" className="Footer__link Footer__linkBtn" onClick={onHomeScroll("top")}>
                  Inicio
                </button>
              ) : (
                <RouterLink className="Footer__link" to={ROUTES.home}>
                  Inicio
                </RouterLink>
              )}
            </li>

            <li>
              <RouterLink className="Footer__link" to={ROUTES.servicios}>
                Servicios
              </RouterLink>
            </li>

            <li>
              {isHome ? (
                <button type="button" className="Footer__link Footer__linkBtn" onClick={onHomeScroll("works")}>
                  Alianzas
                </button>
              ) : (
                <RouterLink className="Footer__link" to={ROUTES.alianzasSeccion}>
                  Alianzas
                </RouterLink>
              )}
            </li>

            <li>
              {isHome ? (
                <button type="button" className="Footer__link Footer__linkBtn" onClick={onHomeScroll("contact")}>
                  Contacto
                </button>
              ) : (
                <RouterLink className="Footer__link" to={ROUTES.contactoSeccion}>
                  Contacto
                </RouterLink>
              )}
            </li>
          </ul>
        </nav>

        <div className="Footer__col" aria-label="Contacto">
          <p className="Footer__colTitle">Contacto</p>
          <ul className="Footer__list">
            <li className="Footer__item">
              <span className="Footer__icon" aria-hidden="true">
                📍
              </span>
              <span>Ciudad de México, México</span>
            </li>

            <li className="Footer__item">
              <span className="Footer__icon" aria-hidden="true">
                📞
              </span>
              <a className="Footer__link" href="tel:+525512345678">
                +52 55 1234 5678
              </a>
            </li>

            <li className="Footer__item">
              <span className="Footer__icon" aria-hidden="true">
                ✉️
              </span>
              <a className="Footer__link" href="mailto:contacto@grupoarca.com">
                contacto@grupoarca.com
              </a>
            </li>
          </ul>

          <div className="Footer__ctaRow">
            <RouterLink className="Footer__btn Footer__btn--primary" to={ROUTES.contacto} aria-label="Solicitar cotización">
              Solicitar cotización
            </RouterLink>
            <a className="Footer__btn Footer__btn--secondary" href="tel:+525512345678" aria-label="Llamar">
              Llamar
            </a>
          </div>
        </div>

        <div className="Footer__col" aria-label="Legal">
          <p className="Footer__colTitle">Legal</p>
          <ul className="Footer__list">
            <li>
              <button type="button" className="Footer__link Footer__linkBtn" aria-label="Aviso de privacidad (pendiente)">
                Aviso de privacidad
              </button>
            </li>
            <li>
              <button type="button" className="Footer__link Footer__linkBtn" aria-label="Términos y condiciones (pendiente)">
                Términos y condiciones
              </button>
            </li>
            <li>
              <button type="button" className="Footer__link Footer__linkBtn" aria-label="Política de cookies (pendiente)">
                Política de cookies
              </button>
            </li>
          </ul>

          <div className="Footer__trust">
            <span className="Footer__trustPill">Calidad verificable</span>
            <span className="Footer__trustPill">Entrega por hitos</span>
            <span className="Footer__trustPill">Cumplimiento</span>
          </div>
        </div>
      </div>

      <div className="Footer__bottom">
        <div className="Footer__bottomInner">
          <p className="Footer__copy">© {year} GRUPO ARCA C&N. Todos los derechos reservados.</p>
          <p className="Footer__micro">Respuesta rápida • Atención profesional • Propuesta clara</p>
        </div>
      </div>
    </footer>
  );
}
